import React, { Fragment } from 'react';

/** Split on bare http(s) URLs in saved chat text (portal payment page, Stripe checkout, etc.). */
const HTTP_URL_IN_TEXT = /(https?:\/\/[^\s]+)/g;

/** Trailing punctuation often gets glued to URLs in pasted or line-broken chat; strip before parsing. */
function stripTrailingUrlNoise(s: string): string {
  let t = s.trimEnd();
  const junk = '.,;:!?)]}>\'"\u201d\u2019';
  while (t.length > 0) {
    const ch = t[t.length - 1];
    if (!junk.includes(ch)) break;
    t = t.slice(0, -1);
  }
  return t;
}

function linkLabelForUrl(u: URL): string {
  const path = (u.pathname || '').toLowerCase();
  const host = u.hostname.toLowerCase();

  if (path.includes('/teleconsult-checkin/payment-success')) {
    return 'Payment completed (portal)';
  }
  if (path.includes('/teleconsult-checkin/payment')) {
    return 'Open payment page';
  }
  if (path.includes('/receipt') || path.includes('/receipts')) {
    return 'View Stripe receipt';
  }

  const isStripeHost =
    host === 'checkout.stripe.com' ||
    host === 'pay.stripe.com' ||
    host === 'stripe.com' ||
    host.endsWith('.stripe.com') ||
    host.endsWith('stripecdn.com') ||
    host.includes('stripecdn.');
  if (isStripeHost) {
    if (host.startsWith('pay.') || path.includes('receipt') || path.includes('invoice')) {
      return 'View Stripe receipt';
    }
    return 'Open Stripe page';
  }

  return 'Open link';
}

function isSafeHttpUrl(u: URL): boolean {
  if (u.protocol === 'https:') return true;
  if (u.protocol === 'http:') {
    const h = u.hostname.toLowerCase();
    return h === 'localhost' || h === '127.0.0.1';
  }
  return false;
}

/** Renders plain text with http(s)://… segments as short, clickable links. */
export function renderChatMessageLinks(text: string): React.ReactNode {
  const parts = text.split(HTTP_URL_IN_TEXT);
  return parts.map((part, i) => {
    if (!/^https?:\/\//i.test(part)) {
      return <Fragment key={i}>{part}</Fragment>;
    }
    const candidate = stripTrailingUrlNoise(part);
    try {
      const u = new URL(candidate);
      if (!isSafeHttpUrl(u)) {
        return <Fragment key={i}>{part}</Fragment>;
      }
      const label = linkLabelForUrl(u);
      // Same tab: `target="_blank"` often opens the system default browser (Electron / embedded WebView),
      // which feels like an "external" browser and loses the staff session context.
      return (
        <a key={i} href={candidate} className="staff-msg-bubble__link" rel="noopener noreferrer">
          {label}
        </a>
      );
    } catch {
      return <Fragment key={i}>{part}</Fragment>;
    }
  });
}
